import React, { useState, useEffect, useMemo } from "react";
import {
    useAddSubcategoryMutation,
    useLazyDownloadCategoriesQuery,
    useLazyGetSystemDefinedCategoryQuery,
    useLazyGetCategoryByParentIdQuery,
    useUpdateCategoriesMutation,
    useChangeCategoriesStatusMutation,
    useLazyGetCategoryQuery
} from "../../../redux/actions/Admin/dashboardAdmin";
import { Plus, Edit2, ArrowLeft, ChevronRight, X, Loader2, Download, Search, Folder, ChevronDown } from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import { IAddSubcategoryRequest } from "../../../types/Request/AdminRequest";
import { DOWNLOAD_CATEGORIES } from "../../../config/apiUrlsAdmin";

interface ICategoryNode {
    id: number;
    name: string;
    level?: number;
    imageUrl?: string;
    isActive?: boolean;
    parentId?: number;
    children?: ICategoryNode[];
}

const CategoriesManagement: React.FC = () => {
    const [activeSectionId, setActiveSectionId] = useState<number | null>(null);
    const [activeSectionTitle, setActiveSectionTitle] = useState<string>("");
    const [selectedParentId, setSelectedParentId] = useState<number>(0);
    // Search Engine States
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [expandedSearchedNodes, setExpandedSearchedNodes] = useState<Record<number, boolean>>({});
    const [navigationStack, setNavigationStack] = useState<ICategoryNode[]>([]);

    // Form Modals Engine States
    const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
    const [editingNode, setEditingNode] = useState<ICategoryNode | null>(null);
    const [name, setName] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [feedback, setFeedback] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // RTK Query Hooks API Core Triggers
    const [triggerGetSystemTabs, { data: systemTabs, isLoading: isLoadingTabs, isError: isTabError }] = useLazyGetSystemDefinedCategoryQuery();
    const [triggerGetChildren, { data: currentChildren, isLoading: isLoadingChildren, isError: isChildError }] = useLazyGetCategoryByParentIdQuery();
    const [triggerDownloadExcel, { isLoading: isDownloadingExcel }] = useLazyDownloadCategoriesQuery();
    const [triggerGetFullTree, { data: fullTreeResponse, isLoading: isLoadingFullTree }] = useLazyGetCategoryQuery();

    const [addSubcategory, { isLoading: isSaving }] = useAddSubcategoryMutation();
    const [updateCategory, { isLoading: isUpdatingNode }] = useUpdateCategoriesMutation();
    const [changeStatus, { isLoading: isChangingStatus }] = useChangeCategoriesStatusMutation();
    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

    const isProcessing = isSaving || isUploading || isDownloadingExcel || isUpdatingNode || isChangingStatus;

    useEffect(() => {
        triggerGetSystemTabs();
    }, [triggerGetSystemTabs]);

    useEffect(() => {
        if (navigationStack.length > 0) {
            const targetParentId = navigationStack[navigationStack.length - 1].id;
            triggerGetChildren(targetParentId);
        } else if (activeSectionId !== null) {
            triggerGetChildren(activeSectionId);
        }
    }, [activeSectionId, navigationStack, triggerGetChildren]);

    useEffect(() => {
        if (activeSectionId !== null) {
            triggerGetFullTree();
        }
    }, [activeSectionId, triggerGetFullTree]);

    const handleSectionClick = (tabId: number, title: string) => {
        setActiveSectionId(tabId);
        setActiveSectionTitle(title);
        setNavigationStack([]);
        setSearchQuery("");
        setFeedback(null);
    };

    const handleDrillDown = (node: ICategoryNode) => {
        setNavigationStack((prev) => [...prev, node]);
        setFeedback(null);
    };

    const handleStepBack = () => {
        setNavigationStack((prev) => prev.slice(0, -1));
        setFeedback(null);
    };

    const handleJumpToBreadcrumb = (index: number) => {
        setFeedback(null);
        if (index === -1) setNavigationStack([]);
        else setNavigationStack((prev) => prev.slice(0, index + 1));
    };

    const handleToggleStatus = async (id: number, currentActiveState: boolean) => {
        if (isProcessing) return;
        try {
            await changeStatus({ id, isActive: { isActive: !currentActiveState } }).unwrap();
            setFeedback({ type: "success", text: "Target reference visibility toggled successfully!" });

            if (navigationStack.length > 0) {
                triggerGetChildren(navigationStack[navigationStack.length - 1].id);
            } else if (activeSectionId !== null) {
                triggerGetChildren(activeSectionId);
            }
            triggerGetFullTree();
        } catch (err) {
            setFeedback({ type: "error", text: "Failed to rewrite status updates on target node index." });
        }
    };

    const handleCategoryImageUpload = async (file: File) => {
        try {
            const response = await uploadFile({
                File: file,
                FolderName: "Categories/Thumbnails"
            }).unwrap();
            setImageUrl(response.fileUrl);
        } catch (error) {
            console.error("Binary media file transfer intercept exception:", error);
            alert("File upload rejected. Check file formatting size limits.");
        }
    };

    const handleOpenCreateModal = () => {
        setEditingNode(null);

        const parentId =
            navigationStack.length > 0
                ? navigationStack[navigationStack.length - 1].id
                : activeSectionId ?? 0;

        setSelectedParentId(parentId);

        setName("");
        setImageUrl("");
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (node: ICategoryNode) => {
        setEditingNode(node);

        const parentId =
            node.parentId ??
            (navigationStack.length > 0
                ? navigationStack[navigationStack.length - 1].id
                : activeSectionId ?? 0);

        setSelectedParentId(parentId);

        setName(node.name);
        setImageUrl(node.imageUrl || "");
        setIsFormModalOpen(true);
    };

    const handleSaveNode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isProcessing || !name.trim()) return;
        setFeedback(null);

        let activeParentId = activeSectionId || 0;
        if (navigationStack.length > 0) {
            activeParentId = navigationStack[navigationStack.length - 1].id;
        }
        console.log(editingNode);
        console.log(activeSectionId);
        console.log(navigationStack); console.log(editingNode);
        console.log(activeSectionId);
        console.log(navigationStack);


        try {
            if (editingNode) {
                await updateCategory({
                    id: editingNode.id,
                    data: {
                        name: name.trim(),
                        imageUrl,
                        isActive: true
                    }
                }).unwrap();
            } else {
                await addSubcategory({
                    name: name.trim(),
                    parentId: selectedParentId,
                    imageUrl,
                    isActive: true
                }).unwrap();
            }
            setIsFormModalOpen(false);
            setName("");
            setImageUrl("");

            triggerGetChildren(activeParentId);
            triggerGetFullTree();
        } catch (err) {
            setFeedback({ type: "error", text: "Database record validation transaction constraint rejected." });
        }
    };

    const handleDownloadExcelGrid = async () => {
        if (isProcessing) return;
        try {
            const response = await fetch(DOWNLOAD_CATEGORIES, {
                method: "GET",
                         credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            if (!response.ok) throw new Error("Failed to download categories excel");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${activeSectionTitle || "System"}_Categories_Matrix.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Excel mapping export operation rejected:", error);
        }
    };

    const toggleSearchNodeExpansion = (id: number) => {
        setExpandedSearchedNodes(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- FIX: TREE TRAVERSAL WITH Descendant Preservation (SHOWS ALL PARENTS AND ALL CHILDREN) ---
    const filteredSearchTree = useMemo(() => {
        if (!searchQuery.trim() || !fullTreeResponse) return [];

        const fullTreeData = fullTreeResponse || [];
        const targetedSectionRoot = fullTreeData.filter((node: any) => node.id === activeSectionId || node.parentId === activeSectionId);

        const traverseAndFilter = (nodes: any[], isParentMatched = false): any[] => {
            const results: any[] = [];

            nodes.forEach(node => {
                // Node matches if its name contains search query OR if any of its parent nodes matched earlier up the tree
                const matchesCurrentText = node.name.toLowerCase().includes(searchQuery.toLowerCase());
                const isMatched = isParentMatched || matchesCurrentText;

                // Pass down whether this branch or its parents matched down the recursive loop
                const filteredChildren = node.children ? traverseAndFilter(node.children, isMatched) : [];

                // Keep node if it matches text, carries matched children, or its parent matched (preserves downstream children)
                if (matchesCurrentText || filteredChildren.length > 0 || (isParentMatched && isMatched)) {
                    results.push({
                        ...node,
                        children: filteredChildren
                    });

                    // Auto-expand ancestral lines so matches display instantly open
                    if (searchQuery.trim().length > 0) {
                        setExpandedSearchedNodes(prev => ({ ...prev, [node.id]: true }));
                    }
                }
            });

            return results;
        };

        return traverseAndFilter(targetedSectionRoot);
    }, [searchQuery, fullTreeResponse, activeSectionId]);

    // Recursive UI node tree item renderer
    const renderSearchNodeTreeItem = (node: any, depth = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = !!expandedSearchedNodes[node.id];
        const isCurrentEnabled = node.isActive !== false;

        return (
            <div key={node.id} className="space-y-1 text-left select-none">
                <div
                    style={{ paddingLeft: `${depth * 1.25}rem` }}
                    className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-xl group border border-transparent hover:border-slate-100 transition"
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        {hasChildren ? (
                            <button
                                onClick={() => toggleSearchNodeExpansion(node.id)}
                                className="p-1 hover:bg-slate-200 rounded-lg text-slate-500 transition cursor-pointer shrink-0"
                            >
                                <ChevronDown size={14} className={`transform transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                            </button>
                        ) : (
                            <div className="w-6 h-6 flex items-center justify-center text-slate-300 shrink-0">
                                <Folder size={13} />
                            </div>
                        )}
                        <span className={`text-xs font-bold truncate ${isCurrentEnabled ? "text-slate-800" : "text-slate-400 line-through"}`}>
                            {node.name}
                        </span>
                        <span className="text-[9px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-400 font-black shrink-0">Tier {node.level || depth + 2}</span>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity pl-2">
                        <button
                            onClick={() => handleOpenEditModal(node)}
                            className="p-1 text-slate-400 hover:text-[#2F5E2E] transition cursor-pointer"
                        >
                            <Edit2 size={12} />
                        </button>
                        <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleToggleStatus(node.id, node.isActive)}
                            className="relative inline-flex h-3.5 w-6 shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none"
                            style={{ backgroundColor: isCurrentEnabled ? "#5FB63F" : "#CBD5E1" }}
                        >
                            <span
                                className="inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow-xs transition duration-200 mt-0.5"
                                style={{ transform: isCurrentEnabled ? "translateX(11px)" : "translateX(1px)" }}
                            />
                        </button>
                    </div>
                </div>

                {hasChildren && isExpanded && (
                    <div className="border-l border-dashed border-slate-200 ml-3.5 mt-0.5 space-y-1">
                        {node.children.map((child: any) => renderSearchNodeTreeItem(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const currentDepth = navigationStack.length;
    const isSearchingActive = searchQuery.trim().length > 0;

    return (
        <div className="px-2 mx-auto pb-10 max-w-7xl relative">

            {isProcessing && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
                        <Loader2 className="w-5 h-5 animate-spin text-mainBrandColor" />
                        <span className="font-semibold text-sm text-slate-700">Writing database cluster changes...</span>
                    </div>
                </div>
            )}

            <div className="mb-6 text-left">
                <h1 className="text-2xl font-black tracking-tight" style={{ color: "#2F5E2E" }}>Category Workspace Manager</h1>
                <p className="text-slate-500 text-xs mt-0.5">Orchestrate parent/child nested nodes down 4 clear structural layers.</p>
            </div>

            {/* TIER 1 TABS SELECTOR */}
            {systemTabs && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {systemTabs.map((tab: any) => {
                        const isActive = activeSectionId === tab.id;
                        return (
                            <button
                                key={tab.id}
                                disabled={isProcessing}
                                onClick={() => handleSectionClick(tab.id, tab.name)}
                                style={{
                                    borderColor: isActive ? "#5FB63F" : "#E2E8F0",
                                    backgroundColor: isActive ? "#ffffff" : "transparent"
                                }}
                                className={`group text-left p-3 rounded-xl border transition-all duration-200 focus:outline-none flex items-center gap-3 disabled:opacity-50 cursor-pointer ${isActive ? "shadow-sm" : "hover:border-slate-300"}`}
                            >
                                <h3 className="text-sm font-black text-slate-800 leading-tight">{tab.name}</h3>
                            </button>
                        );
                    })}
                </div>
            )}

            {feedback && (
                <div className={`mt-4 p-3 rounded-lg text-xs font-semibold border text-left ${feedback.type === "success" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
                    {feedback.text}
                </div>
            )}

            {activeSectionId !== null ? (
                <div className="mt-6 space-y-4">

                    {/* INTEGRATED SEARCH BAR */}
                    <div className="relative w-full max-w-md text-left">
                        <input
                            type="text"
                            placeholder={`Search inside ${activeSectionTitle}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-[#6DBE45] transition"
                        />
                        <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
                    </div>

                    {/* RENDERING SEGMENT A: PRESERVED SUB-TREE SEARCH VIEW */}
                    {isSearchingActive ? (
                        <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4 min-h-[260px]">
                            <div className="border-b border-slate-100 pb-2 text-left">
                                <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
                                    <span>Hierarchical Filter Complete Path Tree</span>
                                    {isLoadingFullTree && <Loader2 className="w-3 h-3 animate-spin text-[#2F5E2E]" />}
                                </h3>
                            </div>

                            <div className="space-y-1">
                                {filteredSearchTree.length === 0 ? (
                                    <p className="text-xs text-slate-400 italic pt-6 text-center">No categories matching "{searchQuery}" inside this branch matrix.</p>
                                ) : (
                                    filteredSearchTree.map((rootNode) => renderSearchNodeTreeItem(rootNode))
                                )}
                            </div>
                        </div>
                    ) : (

                        /* RENDERING SEGMENT B: STANDARD RELATIVE BREADCRUMB EXPLORER GRID */
                        <div className="p-4 rounded-xl border border-slate-200 bg-white">

                            {/* BREADCRUMBS LAYER */}
                            <div className="flex items-center justify-between flex-wrap gap-3 mb-4 bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs font-medium text-slate-600 text-left">
                                <div className="flex items-center flex-wrap gap-2">
                                    <button
                                        disabled={isProcessing}
                                        onClick={() => handleJumpToBreadcrumb(-1)}
                                        className={`hover:text-mainBrandColor font-bold transition-colors disabled:opacity-50 ${currentDepth === 0 ? "text-slate-800" : "text-slate-400"}`}
                                    >
                                        {activeSectionTitle}
                                    </button>

                                    {navigationStack.map((node, index) => (
                                        <div key={node.id} className="flex items-center gap-1.5">
                                            <ChevronRight size={14} className="text-slate-300" />
                                            <button
                                                disabled={index === navigationStack.length - 1 || isProcessing}
                                                onClick={() => handleJumpToBreadcrumb(index)}
                                                className={`hover:underline text-left max-w-[120px] truncate disabled:opacity-50 font-bold ${index === navigationStack.length - 1 ? "text-slate-800" : "text-slate-400"}`}
                                            >
                                                {node.name}
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    disabled={isProcessing}
                                    onClick={handleDownloadExcelGrid}
                                    className="inline-flex items-center gap-1 text-slate-500 hover:text-[#2F5E2E] font-bold uppercase tracking-wider text-[11px] transition disabled:opacity-40 cursor-pointer"
                                >
                                    {isDownloadingExcel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                    <span>Export categories Excel</span>
                                </button>
                            </div>

                            {/* BRANCH TITLE & CREATE ATTACH ATOM */}
                            <div className="flex items-center justify-between gap-3 mb-6 pb-3 border-b border-slate-100 text-left">
                                <div className="flex items-center gap-2">
                                    {currentDepth > 0 && (
                                        <button
                                            disabled={isProcessing}
                                            onClick={handleStepBack}
                                            className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 mr-1 transition text-slate-600 disabled:opacity-40"
                                        >
                                            <ArrowLeft size={14} />
                                        </button>
                                    )}
                                    <h3 className="text-logoHeading text-base font-bold">
                                        {currentDepth === 0 && "Main Categories (Tier 2)"}
                                        {currentDepth === 1 && "Sub-Categories (Tier 3)"}
                                        {currentDepth >= 2 && "Nested Sub-Categories (Tier 4)"}
                                    </h3>
                                </div>

                                <button
                                    disabled={isProcessing}
                                    onClick={handleOpenCreateModal}
                                    className="inline-flex items-center gap-1.5 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white px-3.5 py-2 rounded-xl text-xs font-black shadow-xs cursor-pointer select-none transition"
                                >
                                    <Plus size={15} />
                                    <span>{currentDepth === 0 ? "Add Main Category" : currentDepth === 1 ? "Add Sub-Category" : "Add Nested Option"}</span>
                                </button>
                            </div>

                            {/* RELATIVE CARDS LAYOUT LIST */}
                            {isLoadingChildren && <div className="py-12 text-center text-xs text-slate-400 italic">Running data layer sync queries...</div>}
                            {isChildError && <div className="py-12 text-center text-xs text-red-500 font-medium">Failed to read structural child properties variables.</div>}

                            {!isLoadingChildren && !isChildError && currentChildren && (
                                currentChildren.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
                                        {currentChildren.map((item: any) => {
                                            const isCurrentEnabled = item.isActive !== false;
                                            return (
                                                <div
                                                    key={item.id}
                                                    className={`group relative border rounded-xl transition flex flex-col justify-between min-w-0 p-3 shadow-3xs
    ${item.isActive
                                                            ? "bg-white border-slate-200 hover:border-slate-300"
                                                            : "bg-gray-50 border-gray-200"
                                                        }`}
                                                >
                                                    <div className="w-full h-24 rounded-lg overflow-hidden bg-slate-100 relative">

                                                        <img
                                                            src={item.imageUrl || "/placeholder-category.png"}
                                                            alt={item.name}
                                                            className={`w-full h-full object-cover ${!item.isActive ? "grayscale opacity-50" : ""
                                                                }`}
                                                        />
                                                        <button
                                                            onClick={() => handleOpenEditModal(item)}
                                                            disabled={!item.isActive}
                                                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/95 shadow-xs text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        >
                                                            <Edit2 size={11} />
                                                        </button>
                                                    </div>

                                                    <div className="mt-3 flex flex-col justify-between flex-1">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="min-w-0 flex-1">
                                                                <h4
                                                                    className={`text-sm font-black truncate ${item.isActive
                                                                        ? "text-[#2F5E2E]"
                                                                        : "text-gray-400 line-through"
                                                                        }`}
                                                                >
                                                                    {item.name}
                                                                </h4>
                                                                <p
                                                                    className={`text-[10px] font-mono mt-0.5 font-bold ${item.isActive ? "text-slate-400" : "text-gray-300"
                                                                        }`}
                                                                >
                                                                    ID Key: #{item.id}
                                                                </p>
                                                                {!item.isActive && (
                                                                    <span className="absolute top-3 left-3 bg-red-100 text-red-600 text-[10px] font-bold px-2 py-1 rounded">
                                                                        Disabled
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <button
                                                                type="button"
                                                                disabled={isProcessing}
                                                                onClick={() => handleToggleStatus(item.id, item.isActive)}
                                                                className="relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors duration-200 outline-none mt-0.5"
                                                                style={{ backgroundColor: isCurrentEnabled ? "#5FB63F" : "#CBD5E1" }}
                                                            >
                                                                <span
                                                                    className="pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-xs transition mt-0.5"
                                                                    style={{ transform: isCurrentEnabled ? "translateX(13px)" : "translateX(2px)" }}
                                                                />
                                                            </button>
                                                        </div>

                                                        {currentDepth < 2 && (
                                                            <div className="pt-3 mt-auto">
                                                                <button
                                                                    disabled={!isCurrentEnabled || isProcessing}
                                                                    onClick={() => handleDrillDown(item)}
                                                                    className="w-full h-8 bg-slate-50 hover:bg-slate-100 border border-slate-200 font-bold text-[11px] text-slate-700 rounded-xl transition flex items-center justify-center gap-1 cursor-pointer disabled:opacity-40"
                                                                >
                                                                    <span>{currentDepth === 0 ? "View Sub-Categories" : "Explore Nested Options"}</span>
                                                                    <ChevronRight size={12} className="mt-0.5" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-12 text-center text-xs text-slate-400 italic bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                        This category tier does not contain any sub-categories yet. Click the button above to add one.
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="p-8 rounded-xl border border-dashed border-slate-300 text-center text-slate-400 text-xs mt-6 bg-white italic font-medium">
                    Select an operational segment tab folder from Tier 1 above to open its workspace tree metrics grid.
                </div>
            )}

            {/* FORM MODAL FORM */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md shadow-2xl flex flex-col max-h-[90vh] text-left overflow-hidden">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div>
                                <h3 className="text-sm font-black text-slate-900 tracking-tight">
                                    {editingNode ? `Modify Node: ${editingNode.name}` : currentDepth === 0 ? "Create Tier 2 Main Category" : `Add Child under: ${navigationStack[navigationStack.length - 1]?.name}`}
                                </h3>
                                <p className="text-[9px] text-slate-400 font-black mt-0.5 tracking-wider uppercase">
                                    Target Position Context Branch Depth: Tier {currentDepth + 2}
                                </p>
                            </div>
                            <button
                                type="button"
                                disabled={isProcessing}
                                onClick={() => setIsFormModalOpen(false)}
                                className="p-1.5 rounded-xl text-slate-400 hover:bg-white border border-transparent hover:border-slate-200 cursor-pointer transition disabled:opacity-40"
                            >
                                <X size={15} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveNode} className="p-5 space-y-5 overflow-y-auto">
                            <TextInputComp
                                disabled={isProcessing}
                                value={name}
                                onChangeText={setName}
                                placeholder="Enter structural node text identifier..."
                                showLabel
                                showStar
                                label="Category Title Alpha Designation Label"
                            />

                            <div>
                                <MediaUploadComp
                                    label="Thumbnail Brand Asset Illustration"
                                    value={imageUrl}
                                    onChange={setImageUrl}
                                    onFileSelect={handleCategoryImageUpload}
                                    showStar
                                />
                            </div>

                            <div className="pt-4 flex justify-end gap-2 border-t border-slate-100">
                                <CommonButton
                                    text="Cancel" variant="outline" rounded="lg" className="px-4 text-xs font-semibold"
                                    onClick={() => setIsFormModalOpen(false)} disabled={isProcessing}
                                />
                                <CommonButton
                                    text={isSaving || isUpdatingNode ? "Transmitting payload..." : editingNode ? "Apply Edit Updates" : "Commit Configuration Item"}
                                    variant="filled" rounded="lg"
                                    disabled={isProcessing || !name.trim()}
                                    className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white font-black text-xs px-4 py-2"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesManagement;